/**
 * Modelos genéricos de contrato de prestação de serviço (pt-BR).
 *
 * Cada modelo é uma função pura que recebe os dados do orçamento/cliente/valor
 * e devolve o CORPO do contrato em texto (parágrafos separados por linha em
 * branco). O texto é totalmente editável no modal antes de enviar para
 * assinatura — os modelos servem só de ponto de partida.
 *
 * Datas em America/Sao_Paulo. Nada aqui promete validade ICP-Brasil: a
 * assinatura remota do app é eletrônica com trilha de evidências
 * (MP 2.200-2 / Lei 14.063/2020).
 */

export interface ContractTemplateInput {
  /** Prestador (empresa que oferece o serviço). */
  providerName: string;
  providerDocument?: string | null;
  providerAddress?: string | null;
  /** Tomador (cliente). */
  clientName: string;
  clientDocument?: string | null;
  clientAddress?: string | null;
  /** Descrição do serviço/objeto. */
  serviceDescription?: string | null;
  /** Valor total do serviço. */
  total?: number | null;
  /** Data-base do contrato (ISO). Default: hoje. */
  date?: string | null;
  /** Cidade do foro/assinatura. */
  city?: string | null;
}

export interface ContractTemplate {
  id: string;
  label: string;
  description: string;
  build: (input: ContractTemplateInput) => string;
}

const formatBRL = (value?: number | null): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number.isFinite(value as number) ? (value as number) : 0,
  );

/** Data por extenso em pt-BR (ex.: "22 de julho de 2026"), fuso America/Sao_Paulo. */
const formatLongDate = (iso?: string | null): string => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  });
};

/** Bloco de identificação de uma parte, com placeholders quando faltam dados. */
const partyLine = (name: string, doc?: string | null, address?: string | null): string => {
  const parts = [name?.trim() || '_____________________________'];
  if (doc?.trim()) parts.push(`inscrito(a) no CPF/CNPJ sob nº ${doc.trim()}`);
  if (address?.trim()) parts.push(`com endereço em ${address.trim()}`);
  return parts.join(', ');
};

export const CONTRACT_TEMPLATES: ContractTemplate[] = [
  {
    id: 'prestacao_completo',
    label: 'Prestação de serviços (completo)',
    description: 'Contrato com cláusulas de objeto, valor, prazo, obrigações, rescisão e foro.',
    build: (input) => {
      const provider = partyLine(input.providerName, input.providerDocument, input.providerAddress);
      const client = partyLine(input.clientName, input.clientDocument, input.clientAddress);
      const objeto = input.serviceDescription?.trim() || '_____________________________';
      const valor = formatBRL(input.total);
      const cidade = input.city?.trim() || '__________________';
      const dataExtenso = formatLongDate(input.date);

      return [
        'CONTRATO DE PRESTAÇÃO DE SERVIÇOS',
        '',
        `Pelo presente instrumento particular, de um lado ${provider}, doravante denominado(a) CONTRATADO(A); e, de outro lado, ${client}, doravante denominado(a) CONTRATANTE, têm entre si justo e contratado o seguinte:`,
        '',
        'CLÁUSULA 1ª — DO OBJETO',
        `O presente contrato tem por objeto a prestação dos seguintes serviços pelo(a) CONTRATADO(A) ao(à) CONTRATANTE: ${objeto}.`,
        '',
        'CLÁUSULA 2ª — DO PREÇO E DA FORMA DE PAGAMENTO',
        `Pela prestação dos serviços descritos, o(a) CONTRATANTE pagará ao(à) CONTRATADO(A) o valor total de ${valor}, na forma e nas datas ajustadas entre as partes.`,
        '',
        'CLÁUSULA 3ª — DO PRAZO',
        'Os serviços serão executados no prazo acordado entre as partes, podendo ser prorrogado mediante comum acordo, por escrito.',
        '',
        'CLÁUSULA 4ª — DAS OBRIGAÇÕES DO(A) CONTRATADO(A)',
        'O(A) CONTRATADO(A) obriga-se a executar os serviços com zelo, técnica e qualidade, empregando os materiais e a mão de obra necessários ao bom cumprimento do objeto deste contrato.',
        '',
        'CLÁUSULA 5ª — DAS OBRIGAÇÕES DO(A) CONTRATANTE',
        'O(A) CONTRATANTE obriga-se a fornecer as informações e condições necessárias à execução dos serviços e a efetuar os pagamentos nas datas ajustadas.',
        '',
        'CLÁUSULA 6ª — DA RESCISÃO',
        'O presente contrato poderá ser rescindido por qualquer das partes, mediante comunicação prévia por escrito, respondendo a parte que der causa à rescisão pelos serviços já executados e pelas perdas e danos a que der causa.',
        '',
        'CLÁUSULA 7ª — DO FORO',
        `As partes elegem o foro da comarca de ${cidade} para dirimir quaisquer dúvidas oriundas deste contrato, renunciando a qualquer outro, por mais privilegiado que seja.`,
        '',
        `E, por estarem assim justas e contratadas, as partes assinam o presente instrumento, em ${cidade}, ${dataExtenso}.`,
      ].join('\n');
    },
  },
  {
    id: 'prestacao_simples',
    label: 'Prestação de serviços (simples)',
    description: 'Versão enxuta: objeto, valor e assinatura das partes.',
    build: (input) => {
      const provider = partyLine(input.providerName, input.providerDocument, input.providerAddress);
      const client = partyLine(input.clientName, input.clientDocument, input.clientAddress);
      const objeto = input.serviceDescription?.trim() || '_____________________________';
      const valor = formatBRL(input.total);
      const cidade = input.city?.trim() || '__________________';
      const dataExtenso = formatLongDate(input.date);

      return [
        'CONTRATO DE PRESTAÇÃO DE SERVIÇOS',
        '',
        `CONTRATADO(A): ${provider}.`,
        `CONTRATANTE: ${client}.`,
        '',
        `1. OBJETO. O(A) CONTRATADO(A) prestará ao(à) CONTRATANTE os seguintes serviços: ${objeto}.`,
        '',
        `2. VALOR. Pela prestação dos serviços, o(a) CONTRATANTE pagará o valor total de ${valor}, na forma combinada entre as partes.`,
        '',
        '3. CONDIÇÕES. Os serviços serão executados no prazo e nas condições ajustados entre as partes. Este contrato pode ser rescindido por qualquer das partes mediante aviso prévio, respondendo cada uma pelos serviços já prestados.',
        '',
        `E por estarem de acordo, as partes assinam este contrato em ${cidade}, ${dataExtenso}.`,
      ].join('\n');
    },
  },
];

export const getContractTemplate = (id: string): ContractTemplate | undefined =>
  CONTRACT_TEMPLATES.find((t) => t.id === id);
